import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { logWarn } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Sparkles } from "lucide-react";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import PasswordInput from "@/components/PasswordInput";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [magic, setMagic] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(identifier, password);
      toast.success("Bem-vindo!");
      // Admins e superadmins entram no painel; clientes na área pessoal.
      const isAdmin = u.role === "admin" || u.role === "superadmin";
      nav(isAdmin ? "/admin" : "/minha-conta");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao entrar");
    } finally {
      setLoading(false);
    }
  };

  const sendMagic = async () => {
    if (!magic) { toast.error("Informe seu e-mail"); return; }
    try {
      await api.post("/auth/magic-link", { email: magic });
      setMagicSent(true);
      toast.success("Link enviado! Confira seu e-mail.");
    } catch (err) {
      logWarn("[magic-link] send failed:", err);
      toast.error(err.response?.data?.detail || "Não foi possível enviar o link");
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <div className="max-w-md mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Entrar</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Acesse sua conta</h1>
        <p className="mt-2 text-sm text-neutral-600">Clientes acompanham suas OS · Técnicos e admins vão pro painel automaticamente.</p>

        <div className="mt-8">
          <GoogleLoginButton testid="login-google" />
          <div className="my-6 flex items-center gap-4 text-xs uppercase tracking-widest text-neutral-400">
            <div className="flex-1 h-px bg-neutral-200" />
            ou com e-mail
            <div className="flex-1 h-px bg-neutral-200" />
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <Label>E-mail ou CPF</Label>
            <Input
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="mt-1.5 h-12"
              data-testid="login-identifier"
              autoComplete="username"
              required
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Senha</Label>
              <Link to="/auth/esqueci-senha" className="text-[11px] text-neutral-500 hover:text-neutral-900 underline">Esqueci minha senha</Link>
            </div>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 h-12"
              data-testid="login-password"
              autoComplete="current-password"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-md bg-neutral-900 hover:bg-neutral-800"
            data-testid="login-submit"
          >
            {loading ? "Entrando…" : "Entrar"}
          </Button>
        </form>

        <div className="mt-8 rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-neutral-500">
            <Sparkles size={12}/> Login sem senha
          </div>
          <p className="mt-2 text-sm text-neutral-600">Digite seu e-mail e enviamos um link mágico pra entrar sem senha.</p>
          {magicSent ? (
            <div className="mt-3 flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <Mail size={14} className="mt-0.5 shrink-0"/>
              <span>Se o e-mail estiver cadastrado, o link chega em instantes. Confira também a caixa de spam.</span>
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              <Input
                type="email"
                value={magic}
                onChange={(e) => setMagic(e.target.value)}
                placeholder="seu@email.com"
                className="h-11"
                data-testid="magic-email"
              />
              <Button type="button" onClick={sendMagic} variant="outline" className="h-11 shrink-0" data-testid="magic-submit">
                Enviar link
              </Button>
            </div>
          )}
        </div>

        <div className="mt-8 text-sm text-neutral-600">
          Ainda não tem conta? <Link to="/cadastro" className="underline font-semibold text-neutral-900">Criar conta</Link>
        </div>
      </div>
    </div>
  );
}
